import type { SchedulerBookingValue } from "./types";

const widgetLoadPromises = new Map<string, Promise<void>>();

export function loadWidgetScript(src: string): Promise<void> {
  const cached = widgetLoadPromises.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Cannot load embed script during SSR"));
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset["loaded"] === "true") {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), {
          once: true,
        });
      }
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.addEventListener(
      "load",
      () => {
        script.dataset["loaded"] = "true";
        resolve();
      },
      { once: true }
    );
    script.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), {
      once: true,
    });
    document.head.appendChild(script);
  });

  widgetLoadPromises.set(src, promise);
  return promise;
}

export function recallInviteeValue(
  formValues: Record<string, unknown>,
  blockId: string | null | undefined,
  kind: "name" | "email"
): string {
  if (!blockId) return "";
  const direct = formValues[blockId];
  if (typeof direct === "string" && direct.trim().length > 0) return direct.trim();
  if (kind === "name") {
    const first = formValues[`${blockId}_first_name`];
    const last = formValues[`${blockId}_last_name`];
    const composite = [first, last]
      .filter((v) => typeof v === "string" && v.trim().length > 0)
      .join(" ");
    if (composite.trim().length > 0) return composite.trim();
  } else {
    const email = formValues[`${blockId}_email`];
    if (typeof email === "string" && email.trim().length > 0) return email.trim();
  }
  return "";
}

export function parseStoredBooking(raw: unknown): SchedulerBookingValue | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SchedulerBookingValue>;
    if (!parsed.startTime || !parsed.endTime || !parsed.externalId) return null;
    return {
      startTime: parsed.startTime,
      endTime: parsed.endTime,
      inviteeName: parsed.inviteeName ?? "",
      inviteeEmail: parsed.inviteeEmail ?? "",
      externalId: parsed.externalId,
      externalUrl: parsed.externalUrl ?? null,
      meetUrl: parsed.meetUrl ?? null,
    };
  } catch {
    return null;
  }
}

/** Stable namespace for Cal.com embed instances on a page. */
export function calcomEmbedNamespace(blockId: string): string {
  return `plura_${blockId.replace(/[^a-zA-Z0-9]/g, "").slice(0, 32) || "scheduler"}`;
}
